import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import { Muxer as WebMMuxer, ArrayBufferTarget as WebMArrayBufferTarget } from 'webm-muxer';
import type { Frame } from './frameByFrameRenderer/frame';
import type { Recorder } from './frameByFrameRenderer/types';

type VideoRecorderBufferStats = {
    bufferLength: number;
};

const fps = 30;

/**
 * It renders independent frames to buffer
 */
export class VideoWebcodecsBufferRecorder implements Recorder<HTMLCanvasElement> {
    #frameIndex = 0;

    videoEncoder!: VideoEncoder;
    #muxer!: Muxer<ArrayBufferTarget>;
    #audioBuffer: AudioBuffer;

    constructor({ audioBuffer }: { audioBuffer: AudioBuffer }) {
        this.#audioBuffer = audioBuffer;
    }

    get mp4Buffer() {
        return this.#muxer.target.buffer;
    }

    async start(width: number, height: number) {
        const numberOfChannels = this.#audioBuffer.numberOfChannels;
        const sampleRate = this.#audioBuffer.sampleRate;

        this.#muxer = new Muxer({
            target: new ArrayBufferTarget(),
            video: {
                codec: 'avc',
                width,
                height,
            },
            audio: {
                codec: 'aac',
                numberOfChannels: this.#audioBuffer.numberOfChannels,
                sampleRate: this.#audioBuffer.sampleRate,
            },
            fastStart: 'in-memory',
        });

        this.videoEncoder = new VideoEncoder({
            output: (chunk, meta) => {
                this.#muxer.addVideoChunk(chunk, meta);
            },
            error: (error) => console.error('VideoEncoder error:', error),
        });

        await this.videoEncoder.configure({
            codec: 'avc1.640028',
            width,
            height,
            bitrate: 10_000_000,
            framerate: fps,
        });

        // eslint-disable-next-line no-undef
        const audioEncoder = new AudioEncoder({
            output: (chunk, meta) => {
                this.#muxer.addAudioChunk(chunk, meta);
            },
            error: (e) => console.error(e),
        });
        audioEncoder.configure({
            codec: 'mp4a.40.2',
            sampleRate,
            numberOfChannels,
            bitrate: 128000,
        });

        const length = this.#audioBuffer.duration;
        const data = new Float32Array(length * numberOfChannels * sampleRate);
        for (let i = 0; i < numberOfChannels; i++) {
            data.set(
                this.#audioBuffer.getChannelData(i).subarray(0, data.length),
                i * length * sampleRate,
            );
        }
        // eslint-disable-next-line no-undef
        const audioData = new AudioData({
            format: 'f32-planar',
            sampleRate,
            numberOfFrames: length * sampleRate,
            numberOfChannels,
            timestamp: 0,
            data,
        });
        audioEncoder.encode(audioData);
        audioData.close();
        await audioEncoder.flush();
    }

    async capture(frame: Frame<HTMLCanvasElement>) {
        const videoFrame = new VideoFrame(frame.data, {
            timestamp: (this.#frameIndex * 1_000_000) / fps,
            duration: 1_000_000 / fps,
        });
        const insert_keyframe = this.#frameIndex % 150 === 0;
        this.videoEncoder.encode(videoFrame, { keyFrame: insert_keyframe });
        videoFrame.close();

        this.#frameIndex++;
    }

    async stop(): Promise<VideoRecorderBufferStats> {
        await this.videoEncoder.flush();
        this.#muxer.finalize();

        return {
            bufferLength: this.#muxer.target.buffer.byteLength,
        };
    }
}

export class VideoWebcodecsWebmRecorder implements Recorder<HTMLCanvasElement> {
    #frameIndex = 0;

    videoEncoder!: VideoEncoder;
    #muxer!: WebMMuxer<WebMArrayBufferTarget>;
    #audioBuffer: AudioBuffer;

    constructor({ audioBuffer }: { audioBuffer: AudioBuffer }) {
        this.#audioBuffer = audioBuffer;
    }

    get webmBuffer(): ArrayBuffer {
        return this.#muxer.target.buffer;
    }

    async start(width: number, height: number) {
        const numberOfChannels = this.#audioBuffer.numberOfChannels;
        const sampleRate = this.#audioBuffer.sampleRate;

        this.#muxer = new WebMMuxer({
            target: new WebMArrayBufferTarget(),
            video: {
                codec: 'V_VP9',
                width,
                height,
            },
            audio: {
                codec: 'A_OPUS',
                numberOfChannels: this.#audioBuffer.numberOfChannels,
                sampleRate: this.#audioBuffer.sampleRate,
            },
        });

        this.videoEncoder = new VideoEncoder({
            output: (chunk, meta) => {
                this.#muxer.addVideoChunk(chunk, meta);
            },
            error: (error) => console.error('VideoEncoder error:', error),
        });

        await this.videoEncoder.configure({
            codec: 'vp09.00.10.08',
            width,
            height,
            bitrate: 10_000_000,
            framerate: fps,
        });

        // eslint-disable-next-line no-undef
        const audioEncoder = new AudioEncoder({
            output: (chunk, meta) => {
                this.#muxer.addAudioChunk(chunk, meta);
            },
            error: (e) => console.error(e),
        });
        audioEncoder.configure({
            codec: 'opus',
            sampleRate,
            numberOfChannels,
            bitrate: 128000,
        });

        const length = this.#audioBuffer.duration;
        const data = new Float32Array(length * numberOfChannels * sampleRate);
        for (let i = 0; i < numberOfChannels; i++) {
            data.set(
                this.#audioBuffer.getChannelData(i).subarray(0, data.length),
                i * length * sampleRate,
            );
        }
        // eslint-disable-next-line no-undef
        const audioData = new AudioData({
            format: 'f32-planar',
            sampleRate,
            numberOfFrames: length * sampleRate,
            numberOfChannels,
            timestamp: 0,
            data,
        });
        audioEncoder.encode(audioData);
        audioData.close();
        await audioEncoder.flush();
    }

    async capture(frame: Frame<HTMLCanvasElement>) {
        const videoFrame = new VideoFrame(frame.data, {
            timestamp: (this.#frameIndex * 1_000_000) / fps,
            duration: 1_000_000 / fps,
        });
        const insert_keyframe = this.#frameIndex % 150 === 0;
        this.videoEncoder.encode(videoFrame, { keyFrame: insert_keyframe });
        videoFrame.close();

        this.#frameIndex++;
    }

    async stop(): Promise<VideoRecorderBufferStats> {
        await this.videoEncoder.flush();
        this.#muxer.finalize();

        return {
            bufferLength: this.#muxer.target.buffer.byteLength,
        };
    }
}
